/**
 * Unified source-frame hook for the Screen Viewer.
 *
 * Collapses the three diverging URL/timer effects (JPG poll, MJPG single-URL,
 * MP4/HLS passthrough) that previously lived inline in `screen-viewer.tsx`
 * into one cohesive contract. The Screen Viewer now consumes this hook in
 * place of three parallel `useEffect`s — one place to fix when a render or
 * error pattern changes.
 *
 * Per ADR-0005, all URL construction routes through `buildViewerSourceUrl`
 * from `@polygraf/shared`. This hook is the *temporal* layer on top:
 *
 *   - JPG  → poll the same URL on a fixed interval, with a `?t=…` cache-buster
 *            so the browser doesn't serve a stale image
 *   - MJPG → emit the URL once; the multipart stream owns its own update loop,
 *            unless `mjpgFramePoll` makes it poll single frames as JPG does
 *   - MP4  → emit the URL once; the `<video>` element owns playback
 *   - HLS  → same as MP4
 *
 * A Source with no resolvable endpoint returns `url: null` — the caller
 * surfaces its empty state rather than rendering a frame element.
 *
 * The hook is the single source of truth for "what URL should the renderer
 * use right now"; consumer state (`imageUrl`, `isConnected`, etc.) reads from
 * the returned values instead of being driven by parallel effects.
 */

import {
	deriveSourceFrameBaseUrl,
	isPolledSourceFormat,
	type ScreenFormat,
	type ScreenSource,
} from '@polygraf/shared'
import { useEffect, useRef, useState } from 'react'

/**
 * `null` when the Source has no resolvable endpoint. Consumers should treat
 * `null` as "render the empty state."
 */
export interface UseSourceFrameInput {
	/**
	 * The Source whose endpoint we want to render. `null` while loading the
	 * tRPC service info, or when the URL param is invalid — the hook returns
	 * `url: null` in that case so the consumer can render its empty state.
	 */
	source: ScreenSource | null

	/**
	 * Format selected by the viewer settings. Drives the timer policy and the
	 * generic-proxy endpoint pick (see `buildViewerSourceUrl`).
	 */
	format: ScreenFormat

	/**
	 * Polling interval in seconds for JPG snapshots. Ignored for MJPG / MP4 /
	 * HLS where the underlying transport handles updates itself.
	 */
	refreshInterval: number

	/**
	 * `false` pauses the JPG poll (the user paused). Ignored for non-JPG
	 * formats — they don't poll, they stream.
	 */
	isPlaying: boolean

	/**
	 * `false` → resolve to the raw URL (LAN-direct mode).
	 * `true`  → route through the server proxy. With a `sourceId`, the cloud
	 *           service endpoint is used (visibility-checked + LAN-trusted);
	 *           without one, the format-keyed generic proxy.
	 */
	useProxy: boolean

	/**
	 * The Source's persisted ID. When present and `urlParam` is absent, the
	 * hook routes through `/api/proxy/service/:sourceId` — the cloud-mode
	 * permission-checked endpoint.
	 */
	sourceId?: string

	/**
	 * Set when the viewer is in ad-hoc URL-param mode (`?url=…`). When set,
	 * `sourceId` is ignored even if also present, because ad-hoc mode by
	 * definition has no persisted Source row to look up.
	 */
	urlParam?: string | null

	/**
	 * Server base URL (e.g. `https://api.polygraf.app`). No trailing slash.
	 */
	apiUrl: string

	/**
	 * Poll single frames of an MJPG service instead of playing its stream, for a
	 * viewer that cannot play it (see `resolveMjpgRendering`). `refreshInterval`
	 * and `isPlaying` then apply as they do for JPG.
	 */
	mjpgFramePoll?: boolean
}

export interface UseSourceFrameOutput {
	/**
	 * The URL the renderer should fetch right now. `null` means no frame to
	 * render — the consumer should show its "format not supported" / empty UI.
	 */
	url: string | null

	/**
	 * `true` once the URL has been resolved and emitted at least once.
	 * Mirrors the legacy `isConnected` flag the Screen Viewer header shows.
	 */
	isConnected: boolean

	/**
	 * `true` between mounting and the first URL emission. Once a URL has been
	 * produced, this stays `false` — image-element `onLoad`/`onError` callbacks
	 * own loading state from that point on (the hook can't observe network
	 * progress through an `<img>` tag).
	 */
	isLoading: boolean

	/**
	 * Stamp of the most recent URL emission (the JPG poll updates this on
	 * every interval tick; MJPG/MP4 updates it once at start).
	 */
	lastUpdate: Date | null

	/**
	 * `url` without the per-tick cache-buster — the identity of *what* is being
	 * rendered, as opposed to *which request* is in flight. Stable across JPG
	 * poll ticks, and unlike `url` it survives a pause, so a consumer keying
	 * "have we decoded a frame of this source yet?" off it doesn't re-arm its
	 * warm-up spinner once a second on a perfectly healthy JPG source.
	 */
	baseUrl: string | null
}

/**
 * Append a `t=<now>` cache-buster to a URL. The JPG poll re-fetches the same
 * URL every interval — without the cache-buster the browser's HTTP cache (or
 * an intermediate CDN) can serve a stale frame indefinitely.
 */
function withCacheBuster(baseUrl: string): string {
	const separator = baseUrl.includes('?') ? '&' : '?'
	return `${baseUrl}${separator}t=${Date.now()}`
}

export function useSourceFrame(input: UseSourceFrameInput): UseSourceFrameOutput {
	const {
		source,
		format,
		refreshInterval,
		isPlaying,
		useProxy,
		sourceId,
		urlParam,
		apiUrl,
		mjpgFramePoll,
	} = input

	const [url, setUrl] = useState<string | null>(null)
	const [baseUrl, setBaseUrl] = useState<string | null>(null)
	const [isConnected, setIsConnected] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [lastUpdate, setLastUpdate] = useState<Date | null>(null)

	const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

	useEffect(() => {
		// Clear any pending JPG timer first — every dependency change re-evaluates
		// the policy (e.g. format flipped from JPG → MJPG, or isPlaying → false).
		if (refreshTimerRef.current) {
			clearInterval(refreshTimerRef.current)
			refreshTimerRef.current = null
		}

		const nextBaseUrl = deriveSourceFrameBaseUrl({
			source,
			format,
			useProxy,
			sourceId,
			urlParam,
			apiUrl,
			mjpgFramePoll,
		})

		// Published before the policy branches below, so the identity of the
		// source is available even on the paths that emit no new `url` (a paused
		// JPG poll returns early keeping the last frame on screen).
		setBaseUrl(nextBaseUrl)

		// No URL resolvable (no source / no endpoint) — surface as `url: null`
		// so the consumer renders its own fallback. Don't touch `lastUpdate` —
		// keep the prior frame's timestamp visible if the user toggles formats.
		if (!nextBaseUrl) {
			setUrl(null)
			setIsConnected(false)
			setIsLoading(true)
			return
		}

		if (isPolledSourceFormat(format) || mjpgFramePoll) {
			// JPG: paused → keep the last frame visible but stop the timer.
			if (!isPlaying) return

			const tick = () => {
				setUrl(withCacheBuster(nextBaseUrl))
				setLastUpdate(new Date())
				setIsConnected(true)
				setIsLoading(false)
			}

			tick() // immediate initial frame
			refreshTimerRef.current = setInterval(tick, refreshInterval * 1000)
			return () => {
				if (refreshTimerRef.current) {
					clearInterval(refreshTimerRef.current)
					refreshTimerRef.current = null
				}
			}
		}

		// MJPG / MP4 / HLS: emit the URL once. Multipart streams update
		// themselves; <video> manages playback.
		setUrl(nextBaseUrl)
		setLastUpdate(new Date())
		setIsConnected(true)
		setIsLoading(false)
	}, [
		source,
		format,
		refreshInterval,
		isPlaying,
		useProxy,
		sourceId,
		urlParam,
		apiUrl,
		mjpgFramePoll,
	])

	return { url, baseUrl, isConnected, isLoading, lastUpdate }
}
